// holdBudget.ts — v12.4.16 (#515)
//
// WHAT A TIMEOUT MEANS NOW, AND WHY THIS FILE EXISTS.
//
// #515 was logged because an integration action ran INSIDE the Twilio turn: a 6s timeout with retries could
// spend more than Twilio's ~15s webhook deadline and the call dropped ("Application error occurred" on
// #38-0007, turn total 14,907 ms). The engine half of #515 removed that: an action now starts in the
// background and the turn returns a filler + <Pause> + <Redirect>, so a slow partner holds the caller
// instead of dropping them.
//
// The tenant-facing consequence is a DIFFERENT sentence from the one the defect asked for. A long timeout no
// longer kills the call; it decides HOW LONG A CALLER CAN BE HELD in the hold loop. That is the honest thing
// to put on screen, and it is the sentence both settings screens now carry.
//
// AND THE SPLIT IS HALF THE DEFECT. The action timeout lives in Settings › Reliability; the retry count in
// Settings › Integrations › Failure policy. Neither screen showed the combined effect, so a tenant could set
// each half in perfect good faith and never see the total. Both screens now compute it, from here, so the
// two lines cannot disagree with each other or drift from the engine.
//
// THE ARITHMETIC IS ConnectHoldStore.BudgetFor, MIRRORED EXACTLY:
//     var timeout  = Math.Max(1000, policy.ActionTimeoutMs);
//     var attempts = Math.Max(1, 1 + Math.Max(0, policy.RetryAttempts));
//     var wanted   = (long)timeout * attempts + timeout;          // + one token fetch
//     return (int)Math.Clamp(wanted, MinBudgetMs, MaxBudgetMs);
// The extra timeout is the OAuth token fetch, which runs before the attempts and is bounded by the same
// value. THERE IS NO NEW SETTING HERE and no default is changed: the number falls out of what is already
// stored. If BudgetFor ever changes, this file changes with it — nothing else in T2 does this sum.

/** ConnectHoldStore.MaxBudgetMs — the ceiling a configured total is clamped to. */
export const HOLD_MAX_BUDGET_MS = 60_000;
/** ConnectHoldStore.MinBudgetMs — the floor. */
export const HOLD_MIN_BUDGET_MS = 2_000;
/** EffectiveConfigResolver: an unset/zero Reliability action timeout resolves to 6000 ms. */
export const HOLD_DEFAULT_ACTION_TIMEOUT_MS = 6_000;
/** EffectiveConfigResolver: an absent Integrations retry count resolves to 1. */
export const HOLD_DEFAULT_RETRY_ATTEMPTS = 1;

export interface HoldBudget {
  /** The timeout the engine will actually use (the stored value, floored at 1000 ms). */
  timeoutMs: number;
  /** Attempts = 1 + retries. */
  attempts: number;
  /** timeout x attempts + one timeout for the token fetch, BEFORE the clamp. */
  wantedMs: number;
  /** What the engine will really allow — the clamped figure. */
  budgetMs: number;
  /** True when the configured total is over the ceiling and is being cut back to it. */
  clamped: boolean;
  /** True when the configured total is under the floor and is being raised to it. */
  raised: boolean;
}

export function holdBudget(actionTimeoutMs: number | undefined, retryAttempts: number | undefined): HoldBudget {
  // EffectiveConfigResolver substitutes the defaults first; BudgetFor then applies its own floors.
  const configured = Number.isFinite(actionTimeoutMs) && (actionTimeoutMs as number) > 0
    ? (actionTimeoutMs as number)
    : HOLD_DEFAULT_ACTION_TIMEOUT_MS;
  const retries = Number.isFinite(retryAttempts) ? (retryAttempts as number) : HOLD_DEFAULT_RETRY_ATTEMPTS;

  const timeoutMs = Math.max(1000, Math.round(configured));
  const attempts = Math.max(1, 1 + Math.max(0, Math.round(retries)));
  const wantedMs = timeoutMs * attempts + timeoutMs;
  const budgetMs = Math.min(HOLD_MAX_BUDGET_MS, Math.max(HOLD_MIN_BUDGET_MS, wantedMs));
  return { timeoutMs, attempts, wantedMs, budgetMs, clamped: wantedMs > HOLD_MAX_BUDGET_MS, raised: wantedMs < HOLD_MIN_BUDGET_MS };
}

/** "12.0 s" — seconds to one decimal, because these numbers are configured in milliseconds. */
export function holdSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`;
}

/** "6,000 ms" — the stored value, spelled the way the field spells it. */
export function holdMs(ms: number): string {
  return `${ms.toLocaleString('en-US')} ms`;
}

/**
 * The working, in the tenant's own numbers: what the two settings multiply out to before the clamp.
 * Shown on BOTH screens so neither can imply the other does not exist.
 */
export function holdWorking(b: HoldBudget): string {
  return `${holdMs(b.timeoutMs)} timeout × ${b.attempts} attempt${b.attempts === 1 ? '' : 's'}`
       + ` + ${holdMs(b.timeoutMs)} to fetch an access token = ${holdSeconds(b.wantedMs)}`;
}

/** What the caller actually experiences, and the clamp said plainly when it is happening. */
export function holdOutcome(b: HoldBudget): string {
  if (b.clamped) {
    return `That is over the platform ceiling of ${holdSeconds(HOLD_MAX_BUDGET_MS)}, so a caller is held for at most`
         + ` ${holdSeconds(HOLD_MAX_BUDGET_MS)} and the action is abandoned there — the rest of your setting has no effect.`;
  }
  if (b.raised) {
    return `That is under the platform floor of ${holdSeconds(HOLD_MIN_BUDGET_MS)}, so the action is given`
         + ` ${holdSeconds(HOLD_MIN_BUDGET_MS)} before it is abandoned.`;
  }
  return `A caller can be held for up to ${holdSeconds(b.budgetMs)} while this runs, then the call carries on by your Failure policy.`;
}
